"use client";
import React, { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import FAQSection from "../FAQPage/page";

export default function BlogPage() {
  const pathname = usePathname();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categories, setCategories] = useState(["All"]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch("https://hyreback.hyrelancer.com/api/sitegetBlog", {
          method: "GET",
          headers: {
            "Content-Type": "application/json"
          }
        });
        const data = await response.json();
        if (data.success && data.blogs) {
          const posts = data.blogs.map(blog => ({
            id: blog.blo_id,
            slug: blog.blo_slug,
            image: `https://hyreback.hyrelancer.com/${blog.blo_img}`,
            tag: blog.blog_category?.bca_title || "Uncategorized",
            date: new Date(blog.blo_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) + " . 3 Mins",
            title: blog.blo_title
          }));
          setBlogPosts(posts);

          const uniqueCategories = ["All", ...new Set(posts.map(p => p.tag))];
          setCategories(uniqueCategories);
        }
      } catch (error) {
        console.error("Failed to fetch blogs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  // Generate breadcrumb from pathname
  const generateBreadcrumb = (path) => {
    if (!path) return [];

    const segments = path.split('/').filter(Boolean);
    const breadcrumbs = [{ name: 'Home', path: '/' }];

    let currentPath = '';
    segments.forEach((segment) => {
      currentPath += `/${segment}`;
      // Capitalize first letter and replace hyphens with spaces
      const name = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      breadcrumbs.push({ name, path: currentPath });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumb(pathname);

  // Filter posts based on selected category
  const filteredPosts = selectedCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => post.tag === selectedCategory);

  return (
    <div className="relative min-h-screen bg-white">
      {/* Hero Banner Section */}
      <div
        className="relative w-full max-w-[1920px] mx-auto h-[330px] overflow-hidden flex flex-col items-start justify-center px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-[140px] box-border bg-[length:100%_100%] bg-no-repeat bg-center text-left"
        style={{ backgroundImage: "url('/images/about-us-banner.png')" }}
      >


        <div className="relative z-10 flex flex-col items-start gap-3 md:gap-[19px]">
          <div className="relative text-3xl md:text-5xl lg:text-[48px] xl:text-[52px] 2xl:text-[56px] leading-tight lg:leading-[48px] xl:leading-[52px] 2xl:leading-[56px] font-medium text-white">
            Blog
          </div>
          <div className="relative text-sm md:text-base leading-6 font-semibold text-[#f4f4f4]">
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.path}>
                {index > 0 && <span className="mx-2">|</span>}
                {index === breadcrumbs.length - 1 ? (
                  <span>{crumb.name}</span>
                ) : (
                  <Link
                    href={crumb.path}
                    className="hover:underline transition-opacity hover:opacity-80"
                  >
                    {crumb.name}
                  </Link>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>
      
      {/* Blog Content Section */}
      <div className="w-full max-w-[1920px] mx-auto flex flex-col items-start py-[60px] md:py-[80px] px-6 md:px-12 lg:px-16 xl:px-[144px] gap-[24px] bg-white">
        
        {/* Header */}
        <div className="w-full flex flex-col items-start gap-[12px]">
          <h2 className="text-[#000] text-3xl md:text-[48px] leading-tight md:leading-[48px] font-bold font-['Roboto',sans-serif] m-0">
            Our Blog
          </h2>
          <p className="text-[#000] text-base md:text-[16px] leading-[24px] font-normal font-['Roboto',sans-serif] m-0 max-w-[1628px]">
            Discover insights, ideas, and expert advice to help freelancers and businesses grow faster. Explore the latest trends, practical tips, and success stories from industry professionals.
          </p>
        </div>

        {/* Categories */}
        <div className="w-full flex overflow-x-auto xl:flex-wrap items-center justify-start xl:justify-between gap-4 mt-2 pb-2 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {categories.map((cat, i) => (
            <button
              key={i}
              onClick={() => setSelectedCategory(cat)}
              className={`flex-shrink-0 flex justify-center items-center h-[46px] px-[16px] md:px-[21.467px] gap-[13.417px] rounded-[32.2px] transition-colors ${
                selectedCategory === cat 
                  ? "bg-[#3A599C] text-white" 
                  : "bg-[rgba(58,89,156,0.12)] text-[#3A599C]"
              }`}
            >
              <span className="font-['Roboto',sans-serif] text-[14px] md:text-[18.783px] leading-[20px] md:leading-[32.2px] font-normal whitespace-nowrap">
                {cat}
              </span>
            </button>
          ))}
        </div>

        {/* Blog Cards */}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-[32px] mt-4">
          {loading ? (
            <div className="w-full col-span-1 md:col-span-2 xl:col-span-3 flex justify-center items-center py-20">
              <p className="text-gray-500 font-['Roboto',sans-serif] text-[18px]">Loading blog posts...</p>
            </div>
          ) : filteredPosts.length > 0 ? (
            filteredPosts.map((post) => (
              <Link href={`/blog/${post.slug}`} key={post.id} className="w-full max-w-[523px] flex flex-col p-4 md:p-[24px] rounded-[16px] gap-[25px] border border-[rgba(0,0,0,0.26)] bg-[rgba(255,255,255,0.16)] backdrop-blur-[14.1px] mx-auto group cursor-pointer transition-all">
                {/* Image */}
                <div 
                  className="w-full aspect-[475/338] rounded-[16px] bg-lightgray bg-center bg-cover bg-no-repeat relative flex items-center justify-center overflow-hidden"
                  style={{ backgroundImage: `url('${post.image}')`, backgroundColor: 'lightgray' }}
                >
                  {/* Hover Blur Overlay */}
                  <div className="absolute inset-0 bg-black/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {/* Hover icon */}
                  <div className="relative z-10 w-[64px] h-[64px] bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-md">
                    <ArrowUpRight className="w-8 h-8 text-[#3A599C]" strokeWidth={2.5} />
                  </div>
                </div>

                {/* Content */}
                <div className="w-full flex flex-col items-start gap-[12px]">
                  <div className="flex justify-center items-center py-[8px] px-[16px] bg-[#F4F4F4] rounded-[48px]">
                    <span className="text-[#000] font-['Roboto',sans-serif] text-[14px] leading-[24px] font-normal">
                      {post.tag}
                    </span>
                  </div>
                  
                  <span className="text-[#888] font-['Roboto',sans-serif] text-[14px] leading-[24px] font-normal">
                    {post.date}
                  </span>

                  <h3 className="w-full text-[#000] font-['Roboto',sans-serif] text-[20px] md:text-[24px] leading-[28px] md:leading-[24px] font-semibold m-0">
                    {post.title}
                  </h3>
                </div>
              </Link>
            ))
          ) : (
            <div className="w-full col-span-1 md:col-span-2 xl:col-span-3 flex justify-center items-center py-20">
              <p className="text-gray-500 font-['Roboto',sans-serif] text-[18px]">No blog posts found for this category.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
}
